/* eslint-disable indent */
/* eslint-disable no-nested-ternary */

import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  AccessTime,
  Close,
} from '@mui/icons-material';
import Task from '@mui/icons-material/Task';
import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { alpha } from '@mui/material/styles';
import SelectIcon from '../../../images/unused-undraw-illustrations/undraw_select_option_re_u4qn.svg';
import { setTimezones } from '../../../redux/app/actions';
import {
  addSchedule,
  addScheduleException,
  deleteSchedule,
  updateSchedule,
} from '../../../redux/schedule/actions';
import { primary } from '../../../utils/brandColors';
import { getScheduleBillablePriceError } from '../../../utils/billablePrice';
import { getRepeatEndDate } from '../../../utils/repeatingSchedule';
import {
  buildScheduleTagIds,
  generateEventsFromSchedules,
  getContrast,
} from '../../../utils/scheduleHelper';
import EventActionsMenu from '../../TaskDetail/EventActionsMenu';
import useScheduleSave from '../useScheduleSave';

import HeaderSection from './components/HeaderSection';
import SchedulingSection from './components/SchedulingSection';
import TasksSection from './components/TasksSection';

const startingValidation = {
  eventTemplate: '',
  startDate: '',
  endDate: '',
  assignment: '',
  workflows: '',
};

const AddEditScheduleModal = ({
  open,
  onClose,
  schedule,
  event,
  config,
}) => {
  const dispatch = useDispatch();

  // Redux state
  const isFetchingSchedules = useSelector((state) => state.schedule.isFetchingSchedules);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const allEventTemplates = useSelector((state) => state.eventTemplate.eventTemplates);
  const myTimezone = useSelector((state) => state.app.myTimezone);
  const contextTimezone = useSelector((state) => state.app.contextTimezone);
  const selectedTask = useSelector((state) => state.event.selectedEvent);
  const {
    isSaving,
    hasPendingSave,
    isCurrentScope,
    saveSchedule,
    closeSchedule,
  } = useScheduleSave({
    dispatch,
    onClose,
    isFetchingSchedules,
    scopeKey: JSON.stringify([open, selectedBarn?.id, selectedBarn?.tenant, schedule?.id, event?.start]),
  });

  // React state
  const today = Moment(new Date()).startOf('day').toDate();
  const [eventTemplate, setEventTemplate] = React.useState(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('')
  const [workflows, setWorkflows] = React.useState([]);
  const [assignment, setAssignment] = React.useState('');
  const [type, setType] = React.useState('Default')
  const [startDate, setStartDate] = React.useState(today);
  const [endDate, setEndDate] = React.useState(today);
  const [seriesStartDate, setSeriesStartDate] = React.useState(today);
  const [originalSeriesStartDate, setOriginalSeriesStartDate] = React.useState(today);
  const [duration, setDuration] = React.useState(0);
  const [detailedLocation, setDetailedLocation] = React.useState(null);
  const [rrule, setRrule] = React.useState('RRULE:FREQ=DAILY;INTERVAL=1');
  const [metadata, setMetadata] = React.useState({});
  const [additionalTags, setAdditionalTags] = React.useState([]);
  const [advanceNotice, setAdvanceNotice] = React.useState(0);
  const [timezone, setTimezone] = React.useState('');
  const [timezoneWarning, setTimezoneWarning] = React.useState(false);
  const [validation, setValidation] = React.useState(startingValidation);
  const [savingError, setSavingError] = React.useState('');
  const [overridePastSchedule, setOverridePastSchedule] = React.useState(false);
  const [completeTasksInPast, setCompleteTasksInPast] = React.useState(false);
  const [openSaveOptionsAnchorEl, setOpenSaveOptionsAnchorEl] = React.useState(false);
  const [selectedSaveOption, setSelectedSaveOption] = React.useState(null);
  const [sendInvitationNotifications, setSendInvitationNotifications] = React.useState(true);
  const [confirmRemoveAllTasksForOccurrence, setConfirmRemoveAllTasksForOccurrence] = React.useState(false);
  const [stopRepeating, setStopRepeating] = React.useState(false);

  // Filter out event templates based on config
  const eventTemplates = (schedule || config.eventTemplateTagTypeFilter === -1)
    ? allEventTemplates.filter((et) => !!et.tags.find((t) => selectedBarn && (t.id === selectedBarn.barnMetadata.horseTaskTypeTagId
      || t.id === selectedBarn.barnMetadata.barnTaskTypeTagId)))
    : allEventTemplates.filter((et) => et.id === config.eventTemplateTagTypeFilter
      || et.tags.filter((tag) => tag.id === config.eventTemplateTagTypeFilter).length > 0)

  React.useEffect(() => {
    if (timezone && timezone !== contextTimezone) {
      dispatch(setTimezones(timezone));
    }
    setTimezoneWarning(Moment.tz(myTimezone).zoneAbbr() !== Moment.tz(timezone).zoneAbbr() && `Your timezone ${Moment.tz(myTimezone).zoneAbbr()} (${myTimezone}) is different from the selected timezone ${Moment.tz(timezone).zoneAbbr()} (${timezone}).`);
  }, [timezone])

  const handleSelectEventTemplate = (e) => {
    const selectedEventTemplate = eventTemplates.filter((et) => et.id === e.target.value)[0];
    setEventTemplate(selectedEventTemplate);
    setAdditionalTags(selectedEventTemplate.tags);

    if (!schedule || !schedule.id) {
      setMetadata(selectedEventTemplate.eventDataDefaultValues);
      setAdvanceNotice(parseInt((selectedEventTemplate.eventDataDefaultValues
        && selectedEventTemplate.eventDataDefaultValues.advanceNotice) || 0, 10));
      setName(selectedEventTemplate.name);
      setDescription(selectedEventTemplate.description);
    }

    let startingWorkflows = [];
    if (config.workflows && config.workflows.length) {
      // workflows were passed in
      startingWorkflows = config.workflows;
    } else if (config.taskTagIds.length > 0) {
      // For each additional tag (starting horses or location) - add the workflows
      for (let i = 0; i < config.taskTagIds.length; i += 1) {
        startingWorkflows.push({
          eventTemplateId: selectedEventTemplate.id,
          tagIds: [config.taskTagIds[i].id],
          createEvents: true,
          startingAssignment: config.assignmentTaskMap
            ? ((config.assignmentTaskMap[config.taskTagIds[i].id] && `@[${config.assignmentTaskMap[config.taskTagIds[i].id].userFullName}](${config.assignmentTaskMap[config.taskTagIds[i].id].auth0UserId})`) || '')
            : (config.assignment && `@[${config.assignment.userFullName}](${config.assignment.auth0UserId})`) || '',
          dueDateStatusChangeId: selectedEventTemplate.dueDateStatusChangeId,
          dueDateTime: selectedEventTemplate.dueDateDefaultTime || 0,
          dueDateTimeEndOfDay: selectedEventTemplate.dueDateDefaultTimeEndOfDay || false,
          eventDataDefaultValues: selectedEventTemplate.eventDataDefaultValues || {},
        });
      }
    } else {
      // create empty task
      startingWorkflows.push({
        eventTemplateId: selectedEventTemplate.id,
        tagIds: [],
        createEvents: true,
        startingAssignment: (config.assignment && `@[${config.assignment.userFullName}](${config.assignment.auth0UserId})`) || '',
        dueDateStatusChangeId: selectedEventTemplate.dueDateStatusChangeId,
        dueDateTime: selectedEventTemplate.dueDateDefaultTime || 0,
        dueDateTimeEndOfDay: selectedEventTemplate.dueDateDefaultTimeEndOfDay || false,
        eventDataDefaultValues: selectedEventTemplate.eventDataDefaultValues || {},
      });
    }

    setWorkflows(startingWorkflows);
  };

  const startNewSchedule = (scheduleDate) => {
    setOverridePastSchedule(true);
    setStartDate(scheduleDate || today);
    setEndDate(scheduleDate || today);
  };

  const setFields = (scheduleObject) => {
    // The end date and rrule fields are always linked to the parent
    // If they change we prompt to update the series
    const repeatScheduleToUse = (scheduleObject && scheduleObject.parentSchedule) || scheduleObject;

    setValidation(startingValidation);
    setSavingError('');
    setSelectedSaveOption(null);
    setStopRepeating(false);
    setOverridePastSchedule(false);
    setSendInvitationNotifications(true);
    if (scheduleObject && scheduleObject.id) {
      setEventTemplate(scheduleObject.eventTemplate);
    } else {
      setEventTemplate(null);
    }
    setName((scheduleObject && scheduleObject.id)
      ? scheduleObject.name
      : '');
    setDescription((scheduleObject && scheduleObject.id)
      ? scheduleObject.description
      : '')
    setTimezone((scheduleObject && scheduleObject.id)
      ? scheduleObject.timezone
      : contextTimezone)
    setWorkflows((scheduleObject && scheduleObject.id)
      ? scheduleObject.workflows
      : []);
    setAssignment((scheduleObject && scheduleObject.id)
      ? scheduleObject.assignment
      : null);
    setType((scheduleObject && scheduleObject.id)
      ? scheduleObject.type
      : 'Default');
    setDetailedLocation((scheduleObject && scheduleObject.id)
      ? scheduleObject.location
      : null); // TODO if we add location we can do check here like we do for calendar events
    const newStartDate = (scheduleObject && scheduleObject.id)
      ? new Date((event && event.start) || scheduleObject.startDate)
      : config.startDate || today;
    const nextSeriesStartDate = (repeatScheduleToUse && repeatScheduleToUse.nextOccurrenceDate
      && new Date(repeatScheduleToUse.nextOccurrenceDate).getTime() > new Date().getTime())
      ? new Date(repeatScheduleToUse.nextOccurrenceDate)
      : newStartDate;
    setStartDate(newStartDate);
    setSeriesStartDate(nextSeriesStartDate);
    setOriginalSeriesStartDate(nextSeriesStartDate);
    if (repeatScheduleToUse && repeatScheduleToUse.id) {
      setEndDate(repeatScheduleToUse.endDate
        ? new Date(repeatScheduleToUse.endDate)
        : null);
    } else {
      setEndDate(config.openInSeries
        ? Moment(newStartDate).add(7, 'days').toDate()
        : newStartDate);
    }
    setDuration((scheduleObject && scheduleObject.id)
      ? scheduleObject.duration
      : config.duration || 0);
    setRrule((repeatScheduleToUse && repeatScheduleToUse.id)
      ? repeatScheduleToUse.rrule
      : 'RRULE:FREQ=DAILY;INTERVAL=1');
    setMetadata((scheduleObject && scheduleObject.metadata) || {});
    setAdvanceNotice((scheduleObject && scheduleObject.advanceNotice) || 0);
    setCompleteTasksInPast(false);

    // if event template is null and only one is passed in for options, default select it.
    if (!(scheduleObject && scheduleObject.id) && eventTemplates && eventTemplates.length === 1) {
      handleSelectEventTemplate({ target: { value: eventTemplates[0].id } });
    } else if (scheduleObject && scheduleObject.tags) {
      // since schedule exists, we should filter out the horse and or location tags, those will come in the workflows object
      setAdditionalTags(scheduleObject.tags
        .filter((t) => t.tagType.id !== selectedBarn.barnMetadata.horseTagTypeId && t.tagType.id !== selectedBarn.barnMetadata.locationTagTypeId));
    }
    if (config && config.rescheduleDate) {
      startNewSchedule(config.rescheduleDate);
    }
  };

  React.useEffect(() => {
    setFields(schedule);
  }, [schedule, open, config]);

  const disabledForBeingInPast = (schedule && event && event.end
    && new Date(event.end).getTime() < new Date().getTime()) && !overridePastSchedule

  const validateFields = () => {
    const priceError = getScheduleBillablePriceError({
 workflows,
metadata,
});
    setSavingError(priceError);
    if (priceError) return false;
    const updatedValidation = {
      eventTemplate: '',
      startDate: '',
      endDate: '',
      assignment: '',
      workflows: '',
    };
    let isValid = true;

    if (eventTemplate === null) {
      updatedValidation.eventTemplate = 'Please select a task for this schedule.';
      isValid = false;
    }
    if (workflows.length > 0) {
      for (let i = 0; i < workflows.length; i += 1) {
        if (workflows[i].eventTemplateId === null || workflows[i].eventTemplateId <= 0
          || workflows[i].tagIds === null || workflows[i].tagIds.length === 0) {
          updatedValidation.workflows = 'Required';
          isValid = false;
        }
      }
    } else {
      updatedValidation.workflows = 'Required';
      isValid = false;
    }
    // If schedule is starting in past, don't allow more than 3 recurrences
    // (TODO change backedn process to handle task creation in background to get around this)
    if ((!schedule || !schedule.id || overridePastSchedule) && new Date(startDate).getTime() < new Date() && rrule !== null) {
      const retroEvents = generateEventsFromSchedules([{
        startDate,
        endDate: Moment(new Date()).startOf('day').toDate(),
        id: -1,
        tags: [],
        workflows,
        metadata: {},
        eventTemplate,
        rrule: rrule.replace('RRULE:', ''),
      }], [], startDate, endDate || new Date(), {}, {})
      if (retroEvents.length > 3) {
        updatedValidation.startDate = 'Cannot create a recurring task starting in the past. Please individually create retroactive tasks.';
        isValid = false;
      }
    }

    const effectiveStartDate = disabledForBeingInPast && seriesStartDate
      ? seriesStartDate
      : startDate;
    const selectedStartTime = new Date(effectiveStartDate).getTime();
    const originalOccurrenceStartTime = new Date((event && event.start) || (schedule && schedule.startDate) || startDate).getTime();

    // Don't allow changing an already started schedule to the past
    if (schedule && schedule.id && !overridePastSchedule && selectedStartTime < new Date()
      && selectedStartTime !== originalOccurrenceStartTime) {
      updatedValidation.startDate = 'Cannot change the start date of an already scheduled task to a time in the past.';
      isValid = false;
    }

    if (!!endDate && endDate.getTime() < effectiveStartDate.getTime()) {
      updatedValidation.endDate = 'The schedule end date must be after the start date.';
      isValid = false;
    }

    if (!!duration && duration < 0) {
      updatedValidation.startDate = 'Start time must come before end time.';
      isValid = false;
    }
    setValidation(updatedValidation);
    return isValid;
  };

  const validateOccurrenceChangeFields = () => {
    const priceError = getScheduleBillablePriceError({
 workflows,
metadata,
});
    setSavingError(priceError);
    if (priceError) return false;
    const updatedValidation = {
      eventTemplate: '',
      startDate: '',
      endDate: '',
      assignment: '',
      workflows: '',
    };
    let isValid = true;

    if (workflows.length > 0) {
      for (let i = 0; i < workflows.length; i += 1) {
        if (workflows[i].eventTemplateId === null || workflows[i].eventTemplateId <= 0
          || workflows[i].tagIds === null || workflows[i].tagIds.length === 0) {
          updatedValidation.workflows = 'Required';
          isValid = false;
        }
      }
    }

    setValidation(updatedValidation);
    return isValid;
  };

  const scheduleRepeatChange = () => {
    // Returns true is the start time, duration, end date or rrule changed for a repeating task
    const repeatScheduleToUse = (schedule && schedule.parentSchedule) || schedule;

    if (repeatScheduleToUse && repeatScheduleToUse.id && ((repeatScheduleToUse.endDate !== null && endDate === null)
      || (repeatScheduleToUse.endDate === null && endDate !== null)
      || (repeatScheduleToUse.endDate !== null && endDate !== null
        && new Date(repeatScheduleToUse.endDate).getTime() !== new Date(endDate).getTime())
      || (repeatScheduleToUse.rrule !== rrule))) {
      return true;
    }
    return false;
  }

  const getScheduleChangeData = () => {
    const isNewSchedule = !schedule || !schedule.id || overridePastSchedule;
    const isSingleOccurrenceSchedule = schedule && schedule.id && !schedule.parentSchedule && schedule.endDate != null
      && new Date(schedule.startDate).getTime() === new Date(schedule.endDate).getTime();
    const isRepeatSeriesChange = scheduleRepeatChange();
    const isChangingFromSeriesToSingleOccurrence = schedule && schedule.id && !isSingleOccurrenceSchedule
      && isRepeatSeriesChange
      && event && event.start && new Date(event.start).getTime() === new Date(endDate).getTime();
    const parentScheduleObject = (schedule && schedule.parentSchedule) || schedule;
    const scheduleStartDateForComparison = disabledForBeingInPast && seriesStartDate
      ? seriesStartDate
      : startDate;
    const isHoursChange = (!parentScheduleObject || !parentScheduleObject.id)
      || Moment(scheduleStartDateForComparison).hours() !== Moment(parentScheduleObject.startDate).hours();
    const isMinutesChange = (!parentScheduleObject || !parentScheduleObject.id)
      || Moment(scheduleStartDateForComparison).minutes() !== Moment(parentScheduleObject.startDate).minutes();
    const isSeriesDateTimeChange = !!(disabledForBeingInPast && seriesStartDate && originalSeriesStartDate
      && new Date(seriesStartDate).getTime() !== new Date(originalSeriesStartDate).getTime());
    const isDurationChange = !!(parentScheduleObject && parentScheduleObject.id
      && duration !== parentScheduleObject.duration);

    return {
      isNewSchedule,
      isSingleOccurrenceSchedule,
      isRepeatSeriesChange,
      isChangingFromSeriesToSingleOccurrence,
      parentScheduleObject,
      isTimeChange: isHoursChange || isMinutesChange,
      isSeriesDateTimeChange,
      isDurationChange,
    }
  }

  const scheduleRequiresSeriesSave = () => {
    const scheduleChangeData = getScheduleChangeData();
    return scheduleRepeatChange()
      || (disabledForBeingInPast && (scheduleChangeData.isSeriesDateTimeChange || scheduleChangeData.isDurationChange));
  };

  const hasInvitationParticipants = !!(assignment && assignment.length > 0);
  const showInvitationNotificationOption = hasInvitationParticipants;
  const invitationNotificationLabel = schedule && schedule.id && !overridePastSchedule
    ? 'Email updated invitation to participants'
    : 'Email invitation to participants';
  const getSkipInvitationNotifications = () => (type === 'Invitation' && hasInvitationParticipants
    ? !sendInvitationNotifications
    : false);
  const getOccurrenceStartDate = () => (event && (event.start || event.startDate)
    ? new Date(event.start || event.startDate)
    : startDate);
  const canRemoveAllTasksForOccurrence = !!(schedule && schedule.id && event);

  const handleRemoveAllTasksForOccurrence = () => {
    setConfirmRemoveAllTasksForOccurrence(false);

    if (!schedule || !schedule.id) {
      setWorkflows([]);
      setAssignment('');
      return;
    }

    const isSingleOccurrence = !schedule.parentSchedule && schedule.endDate
      && new Date(schedule.startDate).getTime() === new Date(schedule.endDate).getTime();
    const isScheduleException = schedule.parentSchedule && schedule.parentSchedule.id;
    const deletePromise = (isSingleOccurrence || isScheduleException)
      ? saveSchedule(deleteSchedule(schedule.id))
      : saveSchedule(addScheduleException(schedule.id, {
        exceptionDate: getOccurrenceStartDate(),
        reason: 'Canceled occurrence',
      }, getSkipInvitationNotifications()));

    deletePromise.then(() => {
      closeSchedule(true);
    }).catch((error) => {
      if (!isCurrentScope()) return;
      setSavingError(error.message || 'There was an error attempting to delete the schedule occurrence. Please contact Questri support.');
      console.error(error);
    });
  };

  /*
    We save the entire series when:
      1) Creating a new schedule
      2) This is a single occurrence schedule (no need for schedule exception)
      3) This was a series and use is changing to single occurrence
      4) The Rrule or end date were changed
      5) This is a series and the user selected to update update all future tasks
  */
  const handleSaveEntireSchedule = () => {
    setOpenSaveOptionsAnchorEl(false);
    if (stopRepeating) {
      const parentSchedule = schedule.parentSchedule || schedule;
      const request = { endDate: getRepeatEndDate(schedule, event, selectedTask) };
      if (!request.endDate) {
        setSavingError('The selected occurrence date is unavailable. Reopen this task and try again.');
        return;
      }
      saveSchedule(updateSchedule(parentSchedule.id, request, getSkipInvitationNotifications())).then(() => {
        closeSchedule(true);
      }).catch((error) => {
        if (!isCurrentScope()) return;
        setSavingError(error.message || 'There was an error attempting to stop repeating. Please contact Questri support.');
        console.error(error);
      });
      return;
    }
    if (!validateFields()) { return; }

    const scheduleChangeData = getScheduleChangeData();
    const skipInvitationNotifications = getSkipInvitationNotifications();

    const request = {
      startDate,
      endDate,
      duration,
      description: description || eventTemplate.description,
      assignment: assignment || '',
      name: (name && name.length >= 2)
        ? name
        : eventTemplate.name,
      tags: buildScheduleTagIds({
        workflows,
        additionalTags,
      }),
      workflows,
      location: detailedLocation || null,
      overrideCancellations: false, // TODO implement/change this
      metadata,
      advanceNotice,
      timezone,
      type,
      completeTasksInPast,
      createTasksInPast: true,
    };

    // Adding new schedule
    if (scheduleChangeData.isNewSchedule) {
      saveSchedule(addSchedule(eventTemplate.id, {
        ...request,
        rrule: rrule.replace('RRULE:', ''),
      }, skipInvitationNotifications)).then(() => {
        closeSchedule(true);
      }).catch((error) => {
        if (!isCurrentScope()) return;
        setSavingError(error.message || 'There was an error attempting to add the task. Please contact Questri support.');
        console.error(error);
      });
    } else {
      // If start date or rrule change we need to reload data because the API deletes and recreates a new schedule
      let promise = null;
      // modify entire series
      promise = saveSchedule(updateSchedule((schedule.parentSchedule && schedule.parentSchedule.id) || schedule.id, {
        ...request,
        // We don't allow changing the start date unless this is a single occurrence, changing back to single occurrence, or the hours/minutes changed
        startDate: ((scheduleChangeData.isSingleOccurrenceSchedule || scheduleChangeData.isChangingFromSeriesToSingleOccurrence) && startDate)
          || (scheduleChangeData.isSeriesDateTimeChange && seriesStartDate)
          || (scheduleChangeData.isTimeChange && Moment(scheduleChangeData.parentScheduleObject.startDate)
            .set({
              hour: Moment(startDate).hours(),
              minute: Moment(startDate).minutes(),
            }).toDate())
          || null,
        rrule: rrule === scheduleChangeData.parentScheduleObject.rrule
          ? null
          : rrule.replace('RRULE:', ''),
      }, skipInvitationNotifications));
      promise.then(() => {
        const refreshData = scheduleRepeatChange()
          || scheduleChangeData.isSeriesDateTimeChange
          || scheduleChangeData.isDurationChange
          || new Date(startDate).getTime() !== new Date(schedule.startDate).getTime();
        /*
          If this is an update and we changed the RRULE or start dates, we need to do a refresh of schedules
          because the API would have ended the original schedule and generated a new one which is not returned
          from the patch api call
        */
        closeSchedule(refreshData); // force schedule refresh if needed
      }).catch((error) => {
        if (!isCurrentScope()) return;
        setSavingError(error.message || 'There was an error attempting to update the schedule. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleSaveOccurrence = () => {
    setOpenSaveOptionsAnchorEl(false);
    if (!validateOccurrenceChangeFields()) { return; }

    const request = {
      startDate,
      endDate: startDate,
      duration,
      description: description || eventTemplate.description,
      assignment: assignment || '',
      name: (name && name.length >= 2)
        ? name
        : eventTemplate.name,
      tags: buildScheduleTagIds({
        workflows,
        additionalTags,
      }),
      workflows,
      location: detailedLocation || null,
      overrideCancellations: false, // TODO implement/change this
      metadata,
      advanceNotice,
      timezone,
      type,
      completeTasksInPast,
      createTasksInPast: true,
    };
    const skipInvitationNotifications = getSkipInvitationNotifications();

    const newChildScheduleRequest = {
      ...request,
      parenteScheduleId: (schedule.parentSchedule && schedule.parentSchedule.id) || schedule.id,
      rrule: 'FREQ=DAILY;INTERVAL=1',
    };

    // modify single occurrence in series
    if ((schedule.parentSchedule && schedule.parentSchedule.id)
      || (schedule.endDate && new Date(schedule.startDate).getTime() === new Date(schedule.endDate).getTime())) {
      // if this is a child or a parent but it does not repeat, single occurence, just modify the schedule
      saveSchedule(updateSchedule(schedule.id, newChildScheduleRequest, skipInvitationNotifications))
        .then(() => {
          const refreshData = (new Date(startDate).getTime() !== new Date(schedule.startDate).getTime())
            || (request.tags.sort().toString() !== schedule.tags.map((t) => t.id).sort().toString());
          closeSchedule(refreshData);
        }).catch((error) => {
          if (!isCurrentScope()) return;
          setSavingError(error.message || 'There was an error attempting to update the schedule. Please contact Questri support.');
          console.error(error);
        });
    } else {
      // add schedule exception for original occurrence time
      saveSchedule(addScheduleException(schedule.id, {
        exceptionDate: event.start,
        reason: 'Rescheduled occurrence',
        exceptionSchedule: newChildScheduleRequest,
      }, skipInvitationNotifications)).then(() => {
        closeSchedule(true);
      }).catch((error) => {
        if (!isCurrentScope()) return;
        setSavingError(error.message || 'There was an error attempting to update the schedule. Please contact Questri support.');
        console.error(error);
      });
    }
  }

  const headerBackgroundColor = metadata.calendarDisplayColor || primary.main;
  const headerTextColor = getContrast(metadata.calendarDisplayColor || primary.main);
  const headerTitle = (eventTemplate && eventTemplate.name) || (schedule && schedule.name) || 'Task';
  const eventDateLabel = event && event.start && !overridePastSchedule
    ? Moment(event.start).format('LL')
    : null;

  return (
    // <ModalComponent
    //   TransitionComponent={Transition}
    //   variant="persistent"
    //   open={open}
    //   anchor="right"
    //   hideBackdrop
    //   PaperProps={modalPaperProps}
    //   fullScreen={fullscreen}
    //   onClose={() => onClose(false)}
    //   aria-labelledby="modal-add-schedule-title"
    //   aria-describedby="modal-add-schedule-description"
    // >
    // </ModalComponent>
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: headerBackgroundColor,
          color: `${headerTextColor} !important`,
          m: 1,
          borderRadius: 4,
          backgroundImage: `linear-gradient(130deg, ${headerBackgroundColor}, ${alpha(headerBackgroundColor, 0.85)})`,
          overflow: 'hidden',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${alpha(headerTextColor, 0.4)}`,
                  backgroundColor: `${headerTextColor}1A`,
                  color: headerTextColor,
                }}
              >
                <Task />
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    margin: 0,
                    fontSize: '1.4rem',
                    fontWeight: 600,
                    lineHeight: '1.9rem',
                    width: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {headerTitle}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {eventDateLabel && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <AccessTime
                        sx={{
                          fontSize: 16,
                          color: headerTextColor,
                        }}
                      />
                      <Typography variant="body2" sx={{ color: alpha(headerTextColor, 0.9) }}>
                        {eventDateLabel}
                      </Typography>
                    </Stack>
                  )}
                  <Chip
                    size="small"
                    label={`${(schedule && schedule.id)
                      ? 'Edit'
                      : 'New'} Task`}
                    sx={{
                      backgroundColor: alpha('#000', 0.15),
                      '& .MuiChip-label': { color: headerTextColor },
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
              {
                schedule && schedule.id && !hasPendingSave && (
                  <EventActionsMenu
                    sx={{ color: headerTextColor }}
                    onClose={closeSchedule}
                  />
                )
              }
              <IconButton aria-label="Close schedule" onClick={() => closeSchedule(false)} disabled={isSaving}>
                <Close sx={{ color: headerTextColor }} />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          paddingBottom: 0,
          p: 0,
        }}
      >
        <Grid container>
          <Grid size={12}>
            {
              disabledForBeingInPast && (
                <Alert severity="info" sx={{ m: 1 }}>
                  <AlertTitle>Editing a Task in Progress or Completed</AlertTitle>
                  Historical task details stay locked to preserve completed work.
                  You can update the task list for this occurrence, or change the future date, time,
                  duration, and repeat settings for upcoming tasks in the series.
                </Alert>
              )
            }
            {!disabledForBeingInPast && (
              <HeaderSection
                validation={validation}
                schedule={schedule}
                event={event}
                name={name}
                setName={setName}
                description={description}
                setDescription={setDescription}
                duration={duration}
                workflows={workflows}
                additionalTags={additionalTags}
                setAdditionalTags={setAdditionalTags}
                detailedLocation={detailedLocation}
                setDetailedLocation={setDetailedLocation}
                metadata={metadata}
                setMetadata={setMetadata}
                assignment={assignment}
                setAssignment={setAssignment}
                type={type}
                setType={setType}
                disabledForBeingInPast={disabledForBeingInPast}
                setWorkflows={setWorkflows}
                eventTemplate={eventTemplate}
                taskTypeId={eventTemplate && eventTemplate.id}
              />
            )}

            {
              disabledForBeingInPast && eventTemplate && (
                <Box sx={{ px: 1 }}>
                  <TasksSection
                    schedule={schedule}
                    event={event}
                    eventTemplate={eventTemplate}
                    validation={validation}
                    workflows={workflows}
                    setWorkflows={setWorkflows}
                    disabledForBeingInPast={disabledForBeingInPast}
                    isSaving={isSaving}
                    reminderRefreshKey={JSON.stringify([open, schedule?.id, event?.start])}
                    setAssignment={setAssignment}
                    type={type}
                    name={name}
                    setName={setName}
                    restOfSuggestions={(config && config.restOfSuggestions) || []}
                    onRequestRemoveAllTasksForOccurrence={canRemoveAllTasksForOccurrence
                      ? () => setConfirmRemoveAllTasksForOccurrence(true)
                      : null}
                  />
                </Box>
              )
            }

            <SchedulingSection
              validation={validation}
              schedule={overridePastSchedule
                ? null
                : schedule}
              event={event}
              startDate={disabledForBeingInPast
                ? seriesStartDate
                : startDate}
              endDate={endDate}
              duration={duration}
              eventTemplate={eventTemplate}
              rrule={rrule}
              setStartDate={disabledForBeingInPast
                ? setSeriesStartDate
                : setStartDate}
              setEndDate={setEndDate}
              setRrule={setRrule}
              setAdvanceNotice={setAdvanceNotice}
              setDuration={setDuration}
              timezone={timezone}
              setTimezone={setTimezone}
              disabledForBeingInPast={disabledForBeingInPast}
              defaultOpenRepeat={config && config.openInSeries}
              timezoneWarning={timezoneWarning}
              repeatOnly={disabledForBeingInPast}
              stopRepeating={stopRepeating}
              onStopRepeating={setStopRepeating}
              repeatEndDate={schedule && getRepeatEndDate(schedule, event, selectedTask)}
            />
          </Grid>
          {!disabledForBeingInPast && (
            <Grid size={12}>

              <Divider
                sx={{
                  mb: 2,
                  mt: 2,
                }}
              />
              <Box
                sx={{
                  height: '100%',
                  ml: 0,
                  pl: 2,
                }}
              >
                {
                  !eventTemplate
                    ? (
                      <>
                        <Typography variant="captions" align="center" component="div" sx={{ m: 4 }}>
                          Select a task first...
                        </Typography>
                        <img
                          src={SelectIcon}
                          alt="man selecting something"
                          style={{
                            width: 250,
                            display: 'block',
                            margin: 'auto',
                            marginBottom: '1rem',
                          }}
                        />
                      </>
                    )
                    : (
                      <TasksSection
                        schedule={schedule}
                        event={event}
                        eventTemplate={eventTemplate}
                        validation={validation}
                        workflows={workflows}
                        setWorkflows={setWorkflows}
                        disabledForBeingInPast={disabledForBeingInPast}
                        isSaving={isSaving}
                        reminderRefreshKey={JSON.stringify([open, schedule?.id, event?.start])}
                        setAssignment={setAssignment}
                        type={type}
                        name={name}
                        setName={setName}
                        restOfSuggestions={(config && config.restOfSuggestions) || []}
                        onRequestRemoveAllTasksForOccurrence={canRemoveAllTasksForOccurrence
                          ? () => setConfirmRemoveAllTasksForOccurrence(true)
                          : null}
                      />
                    )
                }
              </Box>
            </Grid>
          )}

        </Grid>

        {
          ((!schedule || !schedule.id) && (new Date().getTime() - startDate.getTime() > 90000000)) && (
            <Alert variant="outlined" severity="warning" mt={2}>
              <AlertTitle>Scheduling a Retro Task</AlertTitle>
              <Box>
                You are scheduling a retroactive task, configure how any past tasks should behave:
              </Box>
              <FormGroup>
                <FormControlLabel sx={{ m: 0 }} control={<Checkbox checked={completeTasksInPast} onChange={(e) => setCompleteTasksInPast(e.target.checked)} />} label="Mark Past Task(s) Complete?" />
              </FormGroup>
            </Alert>
          )
        }

        {savingError && <Alert variant="outlined" severity="error">{savingError}</Alert>}
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'stretch',
          flexDirection: 'column',
        }}
      >
        {
            showInvitationNotificationOption && (
              <Box sx={{ width: '100%' }}>
                <FormControlLabel
                  label={invitationNotificationLabel}
                  sx={{ m: 0 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={type === 'Invitation' && sendInvitationNotifications}
                      onChange={(e) => {
                        setType('Invitation');
                        setSendInvitationNotifications(e.target.checked);
                      }}
                      inputProps={{ 'aria-label': invitationNotificationLabel }}
                    />
                  )}
                />
              </Box>
            )
          }
        <Box
          sx={{
              display: 'flex',
              width: '100%',
          }}
        >
          <Button onClick={() => closeSchedule(false)} disabled={isSaving} sx={{ mr: 1 }}>Cancel</Button>
          <Button
            sx={{ flex: 1 }}
            onClick={() => {
                if (stopRepeating) {
                  setOpenSaveOptionsAnchorEl(true);
                  return;
                }
                const scheduleChangeData = getScheduleChangeData();
                if (scheduleChangeData.isNewSchedule || scheduleChangeData.isSingleOccurrenceSchedule
                  || scheduleChangeData.isChangingFromSeriesToSingleOccurrence) {
                  // If new schedule, single occurrence, or moving from series to single occurrence, we can just save
                  handleSaveEntireSchedule()
                } else {
                  setOpenSaveOptionsAnchorEl(true)
                }
            }}
            disabled={isSaving || !eventTemplate}
            variant="contained"
            color="primary"
            loading={isSaving}
            loadingPosition="start"
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
      <Dialog
        open={openSaveOptionsAnchorEl}
        onClose={() => {
          setOpenSaveOptionsAnchorEl(false)
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {stopRepeating
            ? 'Stop repeating this task?'
            : 'Edit Recurring Task'}
        </DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          {
            !stopRepeating && schedule && schedule.id && startDate && (
              !scheduleRequiresSeriesSave()
                ? (
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      defaultValue="female"
                      name="radio-buttons-group"
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Radio
                            checked={selectedSaveOption === 'event'}
                            onClick={() => {
                              setSelectedSaveOption('event')
                            }}
                          />
                        )}
                        label={`This Task (${Moment(startDate).format('ll')})`}
                      />
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={(
                          <Radio
                            checked={selectedSaveOption === 'all'}
                            onClick={() => {
                              setSelectedSaveOption('all')
                            }}
                          />
                        )}
                        label={<span>&#42;All Tasks</span>}
                      />
                    </RadioGroup>
                    <FormHelperText>
                      &#42;Only scheduled (future) tasks will be changed.
                      Tasks that are already created will not be modified.
                    </FormHelperText>
                  </FormControl>
                )
                : (
                  <Alert severity="info">
                    You are changing the date, time, duration, frequency, or end date on this repeating task.
                    All future occurrences will be modified.
                  </Alert>
                )
            )
          }
          {stopRepeating && (
            <Alert severity="info">
              Repeating will end after {Moment(getRepeatEndDate(schedule, event, selectedTask)).format('ll')}.
              This occurrence and past tasks, including completed work, will remain. Later occurrences will be removed.
              Only the repeat ending will be saved.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={isSaving}
            onClick={() => { setOpenSaveOptionsAnchorEl(false) }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={isSaving || (!stopRepeating && !selectedSaveOption && !scheduleRequiresSeriesSave())}
            sx={{ ml: 1 }}
            onClick={() => {
              if (stopRepeating || scheduleRequiresSeriesSave() || selectedSaveOption === 'all') {
                handleSaveEntireSchedule()
              } else {
                handleSaveOccurrence()
              }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmRemoveAllTasksForOccurrence}
        onClose={() => setConfirmRemoveAllTasksForOccurrence(false)}
        aria-labelledby="remove-all-tasks-occurrence-title"
      >
        <DialogTitle id="remove-all-tasks-occurrence-title">
          Delete this task occurrence?
        </DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          All {workflows.length} tasks planned for {Moment(getOccurrenceStartDate()).format('ll')} will be removed.
          Future scheduled tasks in the repeating series will remain.
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={isSaving}
            onClick={() => setConfirmRemoveAllTasksForOccurrence(false)}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            size="small"
            disabled={isSaving}
            onClick={handleRemoveAllTasksForOccurrence}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

AddEditScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  schedule: PropTypes.object,
  event: PropTypes.object,
  config: PropTypes.object,
};

AddEditScheduleModal.defaultProps = {
  schedule: null,
  event: null,
  config: {},
};

export default AddEditScheduleModal;
